import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { useZone } from "@/components/session-provider";
import { formatDate } from "@/lib/time";
import { ApiError } from "@/lib/api";
import {
  CONTENT_TYPE_LABELS,
  getCollection,
  type KnowledgeCollectionContents,
} from "@/lib/knowledge";

export const Route = createFileRoute("/kb/$collection/")({ component: Collection });

/**
 * One shelf, as a table of contents.
 *
 * ⚠️ **This route is `/kb/$collection` and the authoring screen is `/kb/manage`.** The router
 * ranks a static segment above a dynamic one, so `manage` reaches the editor rather than a
 * collection — which also means a collection whose slug is literally `manage` would be
 * unreachable. Worth knowing before somebody names one that.
 *
 * ⚠️ **An index file, for the reason `kb.index.tsx` carries in full.** As
 * `kb.$collection.tsx` this was the parent of `kb.$collection.$slug.tsx` and drew no
 * `<Outlet />`, so opening an article from a shelf changed the URL and redrew the shelf.
 */
function Collection() {
  const { collection: slug } = Route.useParams();
  const zone = useZone();

  const [contents, setContents] = useState<KnowledgeCollectionContents | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setFailed(null);

    void getCollection(slug)
      .then((data) => {
        if (live) setContents(data);
      })
      .catch((error: unknown) => {
        if (!live) return;
        // ⚠️ A 404 here is "no such collection, or none you can open" — the two are one answer
        // on purpose, so this screen can never become a way to learn which shelves exist.
        setFailed(
          error instanceof ApiError && error.status === 404
            ? "That part of the knowledge base could not be found."
            : "This collection could not be loaded.",
        );
      })
      .finally(() => {
        if (live) setLoading(false);
      });

    return () => {
      live = false;
    };
  }, [slug]);

  return (
    <div className="mx-auto w-full max-w-4xl space-y-6 p-4 sm:p-6">
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-1 text-[12px] text-muted-foreground"
      >
        <Link to="/kb" className="hover:text-foreground">
          Knowledge base
        </Link>
        <ChevronRight className="size-3" aria-hidden />
        <span className="text-foreground">{contents?.collection.name ?? slug}</span>
      </nav>

      {loading ? (
        <p className="text-[13px] text-muted-foreground">Loading…</p>
      ) : failed !== null ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center">
          <p className="text-[13px] font-medium">{failed}</p>
          <Link
            to="/kb"
            className="mt-2 inline-block text-[12px] text-primary underline underline-offset-2"
          >
            Back to the knowledge base
          </Link>
        </div>
      ) : contents !== null ? (
        <>
          <header className="space-y-1">
            <h1 className="text-xl font-semibold tracking-tight">{contents.collection.name}</h1>
            {contents.collection.description ? (
              <p className="text-[13px] text-muted-foreground">{contents.collection.description}</p>
            ) : null}
          </header>

          {contents.articles.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-8 text-center">
              <p className="text-[13px] font-medium">Nothing has been published here yet</p>
              <p className="mt-1 text-[12px] text-muted-foreground">
                Raise a request in the meantime and the service desk will help.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border">
              {contents.articles.map((article) => (
                <li key={article.slug}>
                  <Link
                    to="/kb/$collection/$slug"
                    params={{ collection: article.collectionSlug, slug: article.slug }}
                    className="block p-4 transition-colors hover:bg-muted/50"
                  >
                    <p className="text-[13px] font-medium">{article.title}</p>
                    {article.summary ? (
                      <p className="mt-1 text-[12px] text-muted-foreground">{article.summary}</p>
                    ) : null}
                    <p className="mt-2 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
                      <span className="rounded border border-border px-1.5 py-0.5">
                        {CONTENT_TYPE_LABELS[article.contentType]}
                      </span>
                      {article.appliesTo ? <span>{article.appliesTo}</span> : null}
                      <span>· Updated {formatDate(article.updatedAtUtc, zone)}</span>
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </div>
  );
}
